const RUNTIME_CONFIG_PATH = '/assets/data/config.json';

const REQUIRED_RUNTIME_KEYS = [
  'API_TEMPLATE',
  'WEBSOCKET_ENDPOINT',
  'THREATMETRIX_HOST_URL',
  'THREATMETRIX_ORIGIN_ID',
] as const;

type RuntimeConfigKey = typeof REQUIRED_RUNTIME_KEYS[number];

export type RuntimeConfig = Record<string, string> & {
  [K in RuntimeConfigKey]: string;
};

let runtimeConfig: RuntimeConfig | null = null;

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeRuntimeConfig = (value: unknown): RuntimeConfig => {
  if (!isObjectRecord(value)) {
    throw new Error('Runtime config payload must be a JSON object.');
  }

  const normalizedConfig = Object.entries(value).reduce<Record<string, string>>((accumulator, [key, rawValue]) => {
    if (typeof rawValue === 'string') {
      accumulator[key] = rawValue;
      return accumulator;
    }

    if (rawValue !== null && rawValue !== undefined) {
      accumulator[key] = String(rawValue);
    }

    return accumulator;
  }, {});

  const missingKeys = REQUIRED_RUNTIME_KEYS.filter((key) => !normalizedConfig[key]);
  if (missingKeys.length > 0) {
    throw new Error(`Runtime config is missing required keys: ${missingKeys.join(', ')}`);
  }

  return normalizedConfig as RuntimeConfig;
};

export const loadRuntimeConfig = async (configPath = RUNTIME_CONFIG_PATH): Promise<RuntimeConfig> => {
  const response = await fetch(configPath, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load runtime config from ${configPath}. Status: ${response.status}`);
  }

  const payload = await response.json();
  runtimeConfig = normalizeRuntimeConfig(payload);
  return runtimeConfig;
};

export const getRuntimeConfig = (): RuntimeConfig => {
  if (!runtimeConfig) {
    throw new Error('Runtime configuration has not been loaded yet.');
  }

  return runtimeConfig;
};

export const getRuntimeConfigValue = (key: RuntimeConfigKey): string => getRuntimeConfig()[key];
